import { useRef, type ReactNode } from 'react';
import { useCsvFileDrop } from '@/hooks/useCsvFileDrop';
import { UploadIcon } from './UploadIcon';

interface MateriaalUploadControlProps {
  faseId: number;
  faseName: string;
  canUpload: boolean;
  /** Whether the CAD JSON button is offered alongside the CSV upload. */
  allowCad: boolean;
  isUploading: boolean;
  onUpload: (file: File) => Promise<void>;
  onCadClick: () => void;
  onError: (msg: string) => void;
  /** Fase header (expand button) — rendered in the controls row. */
  header: ReactNode;
  /** Expanded fase body (child toggles + picking tables) — rendered inside the
   *  same drop-target wrapper so the drag-over ring covers the whole fase. */
  body?: ReactNode;
}

/**
 * CSV-upload button + CAD JSON button + hidden file input + native drag-drop.
 * Owns the drop-target wrapper. The fase header and body are passed in so the
 * drag-over ring spans the entire fase, matching the original FaseSection DOM.
 * Dumb: upload + CAD + error are delegated via callbacks.
 */
export function MateriaalUploadControl({
  faseId,
  faseName,
  canUpload,
  allowCad,
  isUploading,
  onUpload,
  onCadClick,
  onError,
  header,
  body,
}: MateriaalUploadControlProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  void faseId;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const { isDragOver, dropHandlers } = useCsvFileDrop({
    enabled: canUpload && !isUploading,
    onFile: onUpload,
    onError,
  });

  return (
    <div
      {...(canUpload ? dropHandlers : {})}
      className={`rounded-xl transition-colors ${
        isDragOver ? 'ring-2 ring-dashed ring-td-blue bg-td-blue/5' : ''
      }`}
    >
      <div className="flex items-center gap-0">
        {header}
        {canUpload && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="ml-2 inline-flex items-center gap-1.5 bg-td-blue/10 text-td-blue hover:bg-td-blue/20 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
              aria-label={`Upload CSV voor ${faseName}`}
              title="Klik om te uploaden, of sleep een CSV-bestand op deze fase"
            >
              {isUploading ? (
                <div className="w-3.5 h-3.5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
              ) : (
                <UploadIcon className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">Upload CSV</span>
            </button>
            {allowCad && (
              <button
                type="button"
                onClick={onCadClick}
                disabled={isUploading}
                className="ml-2 inline-flex items-center gap-1.5 bg-td-green/10 text-td-green hover:bg-td-green/20 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
                aria-label={`Importeer CAD JSON voor ${faseName}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">CAD JSON</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              aria-label={`CSV bestand selecteren voor ${faseName}`}
            />
          </>
        )}
      </div>

      {body}
    </div>
  );
}
